import { useCallback, useEffect, useRef, useState } from 'react';
import { wsUrl } from '@/utils/commsApi';

const PING_MS = 25000;
const MAX_BACKOFF_MS = 15000;

// status: connecting | connected | reconnecting | offline
export function useCommsSocket(userId, onEvent) {
  const [status, setStatus] = useState('connecting');
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;
  const wsRef = useRef(null);

  const send = useCallback((payload) => {
    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify(payload));
  }, []);

  useEffect(() => {
    if (!userId) return undefined;

    let ws = null;
    let closed = false;
    let attempt = 0;
    let reconnectTimer = null;
    let pingTimer = null;

    const stopPing = () => { clearInterval(pingTimer); pingTimer = null; };

    const connect = () => {
      if (closed) return;
      if (!navigator.onLine) { setStatus('offline'); return; }

      ws = new WebSocket(wsUrl(userId));
      wsRef.current = ws;

      ws.onopen = () => {
        attempt = 0;
        setStatus('connected');
        onEventRef.current({ type: '__connected' });
        stopPing();
        pingTimer = setInterval(() => {
          if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'ping' }));
        }, PING_MS);
      };

      ws.onmessage = (e) => {
        try { onEventRef.current(JSON.parse(e.data)); } catch { /* ignore malformed frames */ }
      };

      ws.onclose = (e) => {
        stopPing();
        if (closed) return;
        if (e.code === 4401 || e.code === 4403) { setStatus('offline'); return; }
        setStatus(navigator.onLine ? 'reconnecting' : 'offline');
        const delay = Math.min(MAX_BACKOFF_MS, 1000 * 2 ** attempt);
        attempt += 1;
        reconnectTimer = setTimeout(connect, delay);
      };

      ws.onerror = () => { try { ws.close(); } catch { /* already closed */ } };
    };

    const handleOnline = () => {
      attempt = 0;
      clearTimeout(reconnectTimer);
      setStatus('reconnecting');
      connect();
    };
    const handleOffline = () => { setStatus('offline'); ws?.close(); };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    connect();

    return () => {
      closed = true;
      stopPing();
      clearTimeout(reconnectTimer);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      ws?.close();
    };
  }, [userId]);

  return { status, send };
}
